import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import CarCard from "@/components/CarCard";
import BookingSearchWidget from "@/components/BookingSearchWidget";
import { SlidersHorizontal, Loader2 } from "lucide-react";
import SEO, { breadcrumbSchema } from "@/components/SEO";

const CATS = ["all", "sedan", "compact_suv", "full_suv"];

const CAT_LABELS = {
  all: "All",
  sedan: "Sedan",
  compact_suv: "Mid-size SUV",
  full_suv: "Full-size SUV",
};

export default function FleetPage() {
  const [searchParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [transmission, setTransmission] = useState("all");
  const [maxPrice, setMaxPrice] = useState(3500);

  useEffect(() => {
    setLoading(true);
    api.get("/cars").then((r) => {
      setCars(r.data);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return cars.filter((c) => {
      if (category !== "all" && c.category !== category) return false;
      if (transmission !== "all" && c.transmission !== transmission) return false;
      if (c.daily_rate > maxPrice) return false;
      return true;
    });
  }, [cars, category, transmission, maxPrice]);

  return (
    <div data-testid="fleet-page">
      <SEO
        title="Our Fleet — Sedans, SUVs & Land Cruisers for Hire in Accra"
        description="Browse Titan Car Rentals' fleet in Ghana. Toyota Camry, Honda CR-V, Toyota Highlander, Hyundai Creta, Toyota Prado, and Toyota Land Cruiser. Daily rates from GH₵600 — book online."
        path="/fleet"
        keywords="car rental fleet Ghana, Toyota Land Cruiser rental Accra, Prado hire Ghana, Toyota Camry rental, Honda CR-V hire, Hyundai Creta Ghana, SUV rental Accra"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Fleet", path: "/fleet" }])}
      />
      {/* Page header */}
      <section className="bg-[#0d2040] text-white py-16 md:py-20 relative overflow-hidden">
        <div className="container-titan relative z-10">
          <span className="eyebrow text-[#ff8a93]">Our Fleet</span>
          <h1 className="h-section mt-2 text-white">Pick your perfect ride.</h1>
          <p className="text-white/70 mt-4 max-w-xl">
            From economy daily drivers to flagship SUVs — every car in our fleet is fully serviced, insured, and ready
            to roll.
          </p>
        </div>
      </section>

      {/* Search widget */}
      <section className="-mt-10 relative z-20">
        <div className="container-titan">
          <BookingSearchWidget />
        </div>
      </section>

      {/* Filters + grid */}
      <section className="section-pad">
        <div className="container-titan grid lg:grid-cols-12 gap-8">
          {/* Filters */}
          <aside className="lg:col-span-3" data-testid="fleet-filters">
            <div className="border border-gray-200 bg-white p-5 sticky top-28">
              <div className="flex items-center gap-2 mb-5">
                <SlidersHorizontal size={16} className="text-[#e63946]" />
                <h3 className="font-display text-lg font-semibold text-[#0d2040]">Filters</h3>
              </div>

              <div className="mb-5">
                <label className="titan-label">Category</label>
                <div className="flex flex-wrap gap-2">
                  {CATS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCategory(c)}
                      className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${
                        category === c
                          ? "bg-[#0d2040] text-white border-[#0d2040]"
                          : "bg-white text-[#0d2040] border-gray-300 hover:border-[#0d2040]"
                      }`}
                      data-testid={`filter-category-${c}`}
                    >
                      {CAT_LABELS[c]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <label className="titan-label">Transmission</label>
                <select
                  value={transmission}
                  onChange={(e) => setTransmission(e.target.value)}
                  className="titan-input"
                  data-testid="filter-transmission"
                >
                  <option value="all">All</option>
                  <option value="automatic">Automatic</option>
                  <option value="manual">Manual</option>
                </select>
              </div>

              <div className="mb-2">
                <label className="titan-label">Max price/day (GH₵)</label>
                <input
                  type="range"
                  min={500}
                  max={3500}
                  step={100}
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(parseInt(e.target.value))}
                  className="w-full accent-[#e63946]"
                  data-testid="filter-max-price"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>GH₵ 500</span>
                  <span className="font-bold text-[#0d2040]">GH₵ {maxPrice}</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-9">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-gray-600">
                Showing <span className="font-bold text-[#0d2040]">{filtered.length}</span> cars
              </p>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-[#e63946]" size={32} />
              </div>
            ) : filtered.length === 0 ? (
              <div className="border border-gray-200 p-12 text-center">
                <h3 className="font-display text-xl text-[#0d2040]">No cars match those filters</h3>
                <p className="text-gray-500 text-sm mt-2">Try widening your search.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
